import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProfileSendService } from 'fym-shared/src/music24/profileSend';
import { catalogConnectPath } from '../../../../mobileApps/mobileAppLinks';
import { useStartPromoChannelConnectMutation } from '../../../../../redux/api/fymAPI';
import { trackEvent } from '../../../../analytics/sml/analytics';
import { useSpotifyCanvasConnect } from '../../../servicesPanel/spotifyCanvas/useSpotifyCanvasConnect';
import { profilePath } from '../useProfileRoute';

/** The router path of a Catalog connect link (no `/app`). Pure. */
export function catalogConnectRoute(service: string): string {
  return catalogConnectPath(service).replace(/^\/app/, '');
}

/**
 * Where a sign-in that leaves the page sends the artist back: the Profile with "Send to all" open
 * (`?sheet=sendAll`) and the artist of the sheet. The server appends the outcome
 * (`promoConnect=&result=`). `/app` is the router basename. Pure.
 */
export function sendAllReturnTo(accountId: number, origin: string = window.location.origin): string {
  return `${origin}/app${profilePath({ sheet: 'sendAll' })}&accountId=${accountId}`;
}

/**
 * "Connect" and "Sign in again" of a service row (design c1 note): the existing connect flows.
 * - Spotify for Artists signs in in place (the Canvas connect) and reads the sheet again.
 * - YouTube and a Facebook Page start the channel sign-in of Promote with the sheet as the return
 *   address, so the artist comes back to the open sheet. One Google sign-in feeds Channels and
 *   Streaming (the shared YouTube connection).
 * - Deezer for Creators signs in with email and password in its Catalog connect.
 */
export function useSendAllConnect({ accountId, onConnected }: { accountId: number; onConnected: () => void }) {
  const spotify = useSpotifyCanvasConnect({ accountId, onConnected });
  const navigate = useNavigate();
  const [startChannelConnect, channelState] = useStartPromoChannelConnectMutation();
  const [failed, setFailed] = useState(false);
  const connectSpotify = spotify.connect;

  const connect = useCallback(
    (service: ProfileSendService) => {
      setFailed(false);
      if (service === 'spotify') {
        connectSpotify();
        return;
      }
      if (service === 'deezer') {
        // eslint-disable-next-line no-void
        void navigate(catalogConnectRoute('deezer'));
        return;
      }
      trackEvent('channel_connect_started', { platform: service });
      startChannelConnect({ accountId, platform: service, returnTo: sendAllReturnTo(accountId) })
        .unwrap()
        .then(({ authorizeUrl }) => window.location.assign(authorizeUrl))
        .catch((error: unknown) => {
          console.error(error);
          setFailed(true);
        });
    },
    [accountId, connectSpotify, navigate, startChannelConnect]
  );

  return { connect, isConnecting: spotify.isConnecting || channelState.isLoading, failed };
}
